import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'

import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import { CloseIcon } from 'src/v3/core/icons'

// Configure PDF.js worker using CDN - more reliable than bundler resolution.
// Must be in the same module as the Document/Page components (still the case in
// react-pdf v11, which is what resolves for the declared "^11.0.0").
// eslint-disable-next-line fp/no-mutation
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const titleID = 'credential-preview-title'
const maxPageWidth = 900

/**
 * Trusts the downloaded blob's MIME type first — the filename is user supplied,
 * so `pdf-scan.jpg` and `card.PDF` both fool a substring match on it. The
 * extension is only a fallback for blobs served without a usable content type.
 */
export function isPDF(blob: Blob, fileName?: string): boolean {
  let mimeType = blob.type.toLowerCase()
  return mimeType === 'application/pdf'
    ? true
    : mimeType.startsWith('image/')
      ? false
      : (fileName || '').toLowerCase().endsWith('.pdf')
}

let PDFPreview = ({ blob }: { blob: Blob }): ReactElement => {
  let [numPages, setNumPages] = useState(0)
  let [hasError, setHasError] = useState(false)
  let [pageWidth, setPageWidth] = useState<number>()
  let containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let container = containerRef.current
    if (!container) return undefined

    let observer = new ResizeObserver(([entry]) =>
      setPageWidth(Math.min(entry.contentRect.width, maxPageWidth)),
    )
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  return (
    <Box ref={containerRef}>
      {hasError ? (
        <Alert severity="error">
          Something went wrong loading this PDF. Download the file to view it.
        </Alert>
      ) : (
        <Document
          file={blob}
          suspense={false}
          onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
          onLoadError={() => setHasError(true)}
          loading={
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: numPages }, (_, index) => (
              <Page
                key={index}
                pageNumber={index + 1}
                width={pageWidth}
                renderTextLayer
                renderAnnotationLayer
              />
            ))}
          </Box>
        </Document>
      )}
    </Box>
  )
}

export let CredentialPreviewDialog = ({
  blob,
  fileName,
  onClose,
}: {
  blob: Blob | null
  fileName?: string
  onClose: () => void
}): ReactElement => {
  let pdf = !!blob && isPDF(blob, fileName)

  let imageURL = useMemo(
    () => (blob && !pdf ? URL.createObjectURL(blob) : undefined),
    [blob, pdf],
  )

  useEffect(
    () => () => {
      if (imageURL) URL.revokeObjectURL(imageURL)
    },
    [imageURL],
  )

  return (
    <Dialog
      open={!!blob}
      onClose={onClose}
      maxWidth={pdf ? 'md' : 'lg'}
      fullWidth={pdf}
      aria-labelledby={titleID}
    >
      <Card
        variant="outlined"
        sx={{ p: 2, position: 'relative', overflow: 'scroll' }}
      >
        <IconButton
          sx={{ position: 'absolute', top: 8, right: 8 }}
          size="small"
          aria-label="Close credential preview"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          id={titleID}
          sx={{
            textAlign: 'center',
            fontSize: 20,
            mb: 0.5,
          }}
        >
          View Credential
        </Typography>
        <Box>
          {blob &&
            (pdf ? (
              <PDFPreview blob={blob} />
            ) : (
              <img src={imageURL} alt="View Credential Image" />
            ))}
        </Box>
      </Card>
    </Dialog>
  )
}
